import { useState } from "react";
import { useContextMenu } from "../hooks/useContextMenu";
import { ContextMenuPortal } from "./common/ContextMenuPortal";
import { CollectionModel, FileInfo } from "../types/models";


interface CollectionWithFiles extends CollectionModel {
    files: FileInfo[];
}

interface DocumentProps {
    selectedFilesDoc: string[];
    documents: { id: string; filename: string }[];
    collections: CollectionWithFiles[];
    selectedCollections: string[];
    addDocument: (documentId: string) => void;
    removeDocument: (documentId: string) => void;
    toggleCollection: (collectionId: string) => void;
    onOpenCollection: () => void;
    onClearAll: () => void;
    isExpanded: boolean;
    onToggleExpanded: () => void;
}

export function Documents({
    selectedFilesDoc,
    documents,
    collections,
    selectedCollections,
    addDocument,
    removeDocument,
    toggleCollection,
    onOpenCollection,
    onClearAll,
    isExpanded,
    onToggleExpanded
}: DocumentProps) {

    const { contextMenuOpen, toggleContextMenu, menuPosition, menuRef, handleMenuAction } = useContextMenu();
    const [showAllCollections, setShowAllCollections] = useState(false);

    // Collections that have files in the current document list
    const relevantCollections = collections.filter(collection =>
        collection.files.some(file => documents.some(doc => doc.id === file.id))
    );

    // When any collections are selected, only show docs belonging to those collections
    const visibleDocuments = selectedCollections.length > 0
        ? documents.filter(doc =>
            collections
                .filter(c => selectedCollections.includes(c.id))
                .some(c => c.files.some(f => f.id === doc.id))
          )
        : documents;

    // Returns all collections a given document belongs to
    const getDocCollections = (docId: string) =>
        collections.filter(c => c.files.some(f => f.id === docId));

    const maxVisibleCollections = isExpanded ? relevantCollections.length : 4;
    const visibleCollections = (showAllCollections || isExpanded)
        ? relevantCollections
        : relevantCollections.slice(0, maxVisibleCollections);
    const hiddenCollectionsCount = relevantCollections.length - maxVisibleCollections;

    const shouldShowViewMore = !isExpanded && (
        relevantCollections.length >= 3 ||
        documents.length >= 8 ||
        hiddenCollectionsCount > 0
    );

    const hasSelections = selectedFilesDoc.length > 0 || selectedCollections.length > 0;

    return (
        <div
            className={`mb-5 pl-2.5 overflow-hidden flex flex-col ${isExpanded ? 'flex-1' : 'flex-1'}`}
            style={isExpanded ? { paddingTop: '40px' } : {}}
        >
            <div className="flex items-center justify-between mb-4 mr-4 flex-shrink-0">
                <p className="text-xs ml-4 font-semibold">DOCUMENTS</p>
                <div className="flex items-center gap-2">
                    {hasSelections && (
                        <button
                            onClick={onClearAll}
                            className="text-xs px-2 py-0.5 rounded-full bg-neutral-500 text-neutral-100 hover:bg-red-600 hover:text-white transition-colors duration-200 font-medium"
                            title="Clear all selections"
                            aria-label="Clear all selections"
                        >
                            Clear
                        </button>
                    )}
                    <button
                        onClick={onOpenCollection}
                        className="bg-transparent border-0 cursor-pointer text-neutral-100 hover:text-red-600 transition-colors duration-200"
                        title="Manage collections"
                        aria-label="Manage collections"
                    >
                        <i className="fas fa-file-import text-sm" />
                    </button>
                </div>
            </div>

            {/* Collection Badges */}
            {relevantCollections.length > 0 && (
                <div className="mb-3 px-4 flex-shrink-0">
                    <div className="flex flex-wrap gap-2">
                        {visibleCollections.map((collection) => {
                            const isSelected = selectedCollections.includes(collection.id);
                            const filesInCollection = collection.files.filter(file =>
                                documents.some(doc => doc.id === file.id)
                            ).length;

                            return (
                                <button
                                    key={collection.id}
                                    onClick={() => toggleCollection(collection.id)}
                                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors duration-200 whitespace-nowrap ${
                                        isSelected
                                            ? 'bg-red-600 text-white'
                                            : 'bg-neutral-500 text-neutral-200 hover:bg-neutral-400'
                                    }`}
                                    title={`${collection.name} — ${filesInCollection} file${filesInCollection !== 1 ? 's' : ''}`}
                                    data-testid="collection-chip"
                                >
                                    {collection.name.length > 16 ? `${collection.name.substring(0, 16)}…` : collection.name}
                                    <span className="ml-1 opacity-70">({filesInCollection})</span>
                                </button>
                            );
                        })}

                        {!isExpanded && showAllCollections && relevantCollections.length > 4 && (
                            <button
                                onClick={() => setShowAllCollections(false)}
                                className="px-3 py-1.5 rounded-full text-xs font-medium bg-neutral-500 text-neutral-200 hover:bg-neutral-400 transition-colors duration-200 whitespace-nowrap"
                                title="Show less"
                            >
                                Show less
                            </button>
                        )}
                    </div>
                </div>
            )}

            {shouldShowViewMore && (
                <div className="mb-3 px-4 flex-shrink-0">
                    <button
                        onClick={onToggleExpanded}
                        className="flex items-center text-xs text-blue-400 hover:text-blue-300 transition-colors duration-200"
                        title="Expand to view all collections and documents"
                    >
                        <i className="fas fa-chevron-right mr-1" />
                        View More
                    </button>
                </div>
            )}

            {isExpanded && (
                <div className="mb-3 px-4 flex-shrink-0">
                    <button
                        onClick={onToggleExpanded}
                        className="flex items-center text-xs text-blue-400 hover:text-blue-300 transition-colors duration-200"
                    >
                        <i className="fas fa-chevron-left mr-1" />
                        View Less
                    </button>
                </div>
            )}

            <div className={`flex-1 overflow-y-hidden pr-2.5 min-h-0 ${!contextMenuOpen ? 'hover:overflow-y-auto' : 'overflow-y-hidden pr-5'}`}>
                {visibleDocuments.length === 0 && selectedCollections.length > 0 ? (
                    <div className="px-4 py-6 text-center text-neutral-400 text-xs">
                        <i className="fas fa-folder-open text-2xl mb-2 block opacity-50" />
                        No documents in selected collection
                    </div>
                ) : (
                    visibleDocuments.map((doc, index) => {
                        const docCollections = getDocCollections(doc.id);
                        const isSelected = selectedFilesDoc.includes(doc.id);
                        return (
                            <div key={index} className="flex items-start relative w-full">
                                <div
                                    onClick={() => addDocument(doc.id)}
                                    className={`flex flex-col w-full overflow-x-hidden transition-colors duration-200 rounded-small m-0.5 p-1.5 pl-2.5 hover:bg-neutral-700 group cursor-pointer ${isSelected ? 'bg-red-600' : ''} ${contextMenuOpen === `document-${index}` ? 'bg-neutral-700' : ''}`}
                                    data-testid="document-checkbox"
                                >
                                    <div className="flex items-center w-full">
                                        <i
                                            className={`fas fa-file text-lg pr-2 pb-0.5 flex-shrink-0 transition-colors duration-200 ${
                                                isSelected
                                                ? 'text-neutral-100 group-hover:text-red-600'
                                                : 'text-red-600 group-hover:text-neutral-100'
                                            }`}
                                        />
                                        <span className="flex-grow w-full text-left whitespace-nowrap overflow-hidden text-ellipsis transition-all duration-200 text-neutral-200 text-sm group-hover:ml-1 select-none">
                                            {doc.filename}
                                        </span>
                                        <button
                                            className="bg-transparent border-0 cursor-pointer h-7 w-5 text-neutral-300 px-2 transition-colors duration-200 hover:text-red-600 active:text-red-600 flex-shrink-0"
                                            onClick={(e) => toggleContextMenu(`document-${index}`, e)}
                                            aria-label="Open document context menu"
                                        >
                                            <i className="fas fa-ellipsis-v text-lg" />
                                        </button>
                                    </div>
                                    {docCollections.length > 0 && (
                                        <div className="flex flex-wrap gap-1 mt-1 ml-5">
                                            {docCollections.map(c => (
                                                <span
                                                    key={c.id}
                                                    className={`text-[10px] px-1.5 py-0.5 rounded-full transition-colors duration-200 ${
                                                        isSelected
                                                            ? 'bg-red-700 text-red-100'
                                                            : 'bg-neutral-500 text-neutral-300 group-hover:bg-neutral-600'
                                                    }`}
                                                >
                                                    {c.name.length > 14 ? `${c.name.substring(0, 14)}…` : c.name}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {contextMenuOpen === `document-${index}` && menuPosition && (
                                    <ContextMenuPortal x={menuPosition.x} y={menuPosition.y}>
                                        <div ref={menuRef} className="bg-neutral-600 border border-neutral-400 shadow-medium w-20 text-left rounded-large overflow-hidden">
                                            <button
                                                className="flex p-2 px-3 w-20 border-0 bg-transparent cursor-pointer text-left text-neutral-white hover:bg-red-600 hover:text-neutral-white"
                                                onClick={handleMenuAction(() => addDocument(doc.id))}
                                            >
                                                {isSelected ? 'Deselect' : 'Select'}
                                            </button>
                                            <button
                                                className="flex p-2 px-3 w-20 border-0 bg-transparent cursor-pointer text-left text-neutral-white hover:bg-red-600 hover:text-neutral-white"
                                                onClick={handleMenuAction(() => removeDocument(doc.id))}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </ContextMenuPortal>
                                )}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}
